import {
  ButtonTabsWithTooltip,
  CustomCheckbox,
  InfiniteScroll,
  ReuseModal,
  ReuseTooltip,
} from "components/ui";
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SearchIcon } from "../../../images/SearchIcon";
import { CustomInput } from "../../CustomInput";
import {
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import {
  ManageSectionsModals,
  ModalSize,
  PlaybookFilter,
  PlaybookPlanNames,
} from "../../../../types/enums";
import {
  useGeneratePlaybooksForAccountsMutation,
  useGetAccountsForPlaybooksQuery,
  useRemoveAccountsPlaybooksMutation,
} from "../../../../api";
import { useParams } from "react-router-dom";
import { useAppDispatch, useDebounceValue } from "../../../../hooks";
import { toast } from "react-toastify";
import { CustomToast } from "../../CustomToast";
import { getErrorMessage } from "../../../../utils/getErrorMessage";
import { setIsNotificationsOpen } from "../../../../features/companySlice";
import { ManageSectionsBlock } from "../../ManageSectionsBlock";
import { TwoPersonIcon } from "../../../images/TwoPersonIcon";
import { GeneratePlaybookListItem } from "../../../../types";
import { AlertInEmptyTriangleIcon } from "../../../images";
import { ConfirmRemovePlaybooksModal } from "./ConfirmRemovePlaybooksModal";

const ListItem = memo(
  ({
    id,
    fullName,
    checked,
    onClick,
    totalCount,
    withAlert,
    isPlaybooksGeneration,
    isDisabled = false,
  }: {
    id: string;
    fullName: string;
    checked: boolean;
    onClick: () => void;
    totalCount?: number;
    withAlert?: boolean;
    isPlaybooksGeneration?: boolean;
    isDisabled: boolean;
  }) => {
    return (
      <MenuItem
        sx={{
          padding: "8px 16px 8px 8px",
          justifyContent: "space-between",
          gap: "16px",
        }}
        value={id}
        onClick={onClick}
        disabled={isDisabled}
      >
        <Stack flexDirection={"row"} alignItems={"center"}>
          <CustomCheckbox
            checked={checked}
            onChange={() => {}}
            disabled={isDisabled}
          />
          <Stack>
            <Typography
              component="span"
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#232323",
                textWrap: "wrap",
              }}
            >
              {fullName}
            </Typography>
            {totalCount !== undefined && (
              <Stack flexDirection={"row"} gap={"8px"} alignItems={"center"}>
                <TwoPersonIcon
                  color={"#232323"}
                  secondColor={"#232323"}
                  width={"12px"}
                  height={"12px"}
                />
                <Typography
                  component="span"
                  sx={{
                    fontSize: "12px",
                    fontWeight: 400,
                    color: "#232323",
                    textWrap: "wrap",
                  }}
                >
                  {totalCount}{" "}
                  {isPlaybooksGeneration
                    ? "Targeted Contacts"
                    : PlaybookPlanNames.playbooks}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
        {withAlert && isPlaybooksGeneration && (
          <ReuseTooltip text={"Missing HG file for this account."}>
            <AlertInEmptyTriangleIcon />
          </ReuseTooltip>
        )}
      </MenuItem>
    );
  },
);

type Props = {
  isOpen: boolean;
  onClose: () => void;
  setIsLoading: (isLoading: boolean) => void;
  withGenerate: boolean;
  isAllowedRemove: boolean;
  isAllowedGenerate: boolean;
  shouldHaveHGData: boolean;
};

const PER_PAGE = 25;

export const ManagePlaybooksModal = ({
  isOpen,
  onClose,
  setIsLoading,
  isAllowedRemove,
  isAllowedGenerate,
  shouldHaveHGData,
}: Props) => {
  const { campaign_id } = useParams();
  const dispatch = useAppDispatch();

  const [currentTab, setCurrentTab] = useState<PlaybookFilter>(
    isAllowedGenerate
      ? PlaybookFilter.without_playbooks
      : PlaybookFilter.with_playbooks,
  );
  const [searchValue, setSearchValue] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [accounts, setAccounts] = useState<GeneratePlaybookListItem[]>([]);
  const [selected, setSelected] = useState<{ [key: string]: boolean }>({});
  const [page, setPage] = useState<number>(1);
  const [isHide, setIsHide] = useState<boolean>(false);
  const [isConfirmModal, setIsConfirmModal] = useState<boolean>(false);

  const wrapper = useRef<HTMLDivElement>(null);

  const debounceValue = useDebounceValue(searchValue);
  const isPlaybooksGeneration = currentTab === PlaybookFilter.without_playbooks;

  const [generatePlaybooks, { isLoading: isGeneratingPlaybooks }] =
    useGeneratePlaybooksForAccountsMutation();

  const [removePlaybooks, { isLoading: isRemovingPlaybooks }] =
    useRemoveAccountsPlaybooksMutation();

  const {
    isLoading: isLoadingMembers,
    isFetching,
    data: cashedData,
    currentData: data,
  } = useGetAccountsForPlaybooksQuery({
    page,
    per_page: PER_PAGE,
    search,
    campaign_id: campaign_id!,
    filter: currentTab,
  });

  const canLoadMore = page < (data?.total_pages || 1);

  useEffect(() => {
    if (debounceValue !== search) {
      setPage(1);
      setSearch(debounceValue);
    }
  }, [debounceValue, search]);

  useEffect(() => {
    setIsLoading(isLoadingMembers);
  }, [isLoadingMembers]);

  useEffect(() => {
    setSelected((prev) => {
      const updated = page === 1 ? {} : { ...prev };
      accounts.forEach(({ id }) => {
        if (!(id in updated)) updated[id] = true;
      });
      return updated;
    });
  }, [accounts]);

  const selectMemberHandler = (id: string) =>
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));

  const isSelectAll =
    accounts.length > 0 && accounts.every((member) => selected[member.id]);

  const toggleAllHandler = () => {
    const checked = !isSelectAll;
    setSelected((prev) =>
      Object.fromEntries(Object.keys(prev).map((key) => [key, checked])),
    );
  };

  const selectedIds = useMemo(
    () =>
      Object.entries(selected)
        .filter(([_, value]) => value)
        .map(([key]) => key),
    [selected],
  );

  const generatePlaybooksHandler = useCallback(() => {
    if (campaign_id) {
      generatePlaybooks({
        campaign_id,
        account_ids: selectedIds,
        include_all: isSelectAll,
      })
        .unwrap()
        .then(() => {
          dispatch(setIsNotificationsOpen(true));
          onClose();
        })
        .catch((err) => {
          toast.error(
            <CustomToast
              type="error"
              text={
                getErrorMessage(err).globalError ||
                "Something went wrong. Please try again"
              }
              title={"Playbooks Generation Failed."}
            />,
          );
        });
    }
  }, [selectedIds, isSelectAll, shouldHaveHGData]);

  const removePlaybooksHandler = useCallback(() => {
    if (campaign_id) {
      removePlaybooks({
        campaign_id,
        account_ids: selectedIds,
        include_all: isSelectAll,
      })
        .unwrap()
        .then(onClose)
        .catch((err) =>
          toast.error(
            <CustomToast
              type="error"
              text={
                getErrorMessage(err).globalError ||
                "Something went wrong. Please try again"
              }
              title={"Failed to Remove Playbooks."}
            />,
          ),
        );
    }
  }, [selectedIds, isSelectAll]);

  const submitHandler = () => {
    isPlaybooksGeneration
      ? generatePlaybooksHandler()
      : setIsConfirmModal(true);
  };

  const changeTabHandler = (tab: string) => {
    setCurrentTab(
      tab === `Generate ${PlaybookPlanNames.playbooks}`
        ? PlaybookFilter.without_playbooks
        : PlaybookFilter.with_playbooks,
    );
    setPage(1);
  };

  const title = `${isPlaybooksGeneration ? "Generate" : "Delete"} ${PlaybookPlanNames.playbooks}`;

  return (
    <ReuseModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Manage ${PlaybookPlanNames.playbooks}`}
      maxWidth={ModalSize.big}
      submitBtnText={title}
      submitBtnColor={isPlaybooksGeneration ? "#1C3C6C" : "#C0392B"}
      onSubmit={submitHandler}
      isLoading={isGeneratingPlaybooks || isRemovingPlaybooks}
      isDisabled={
        isGeneratingPlaybooks || isRemovingPlaybooks || selectedIds.length === 0
      }
      isHide={isHide}
      isDisabledCancelBtn={isGeneratingPlaybooks || isRemovingPlaybooks}
    >
      <Stack gap={"16px"} paddingTop={"16px"}>
        <ButtonTabsWithTooltip
          fullWidth
          currentTab={title}
          onChange={changeTabHandler}
          tabs={[
            {
              label: `Generate ${PlaybookPlanNames.playbooks}`,
              isDisabled: !isAllowedGenerate,
              tooltip: `All available ${PlaybookPlanNames.playbooks} have already been generated.`,
            },
            {
              label: `Delete ${PlaybookPlanNames.playbooks}`,
              isDisabled: !isAllowedRemove,
              tooltip: `There are no ${PlaybookPlanNames.playbooks} to delete at the moment.`,
            },
          ]}
        />

        <InfiniteScroll<GeneratePlaybookListItem>
          hasMore={canLoadMore}
          isLoading={isFetching}
          loadMore={() => setPage((prev) => prev + 1)}
          page={page}
          items={data?.items || []}
          scrollOffset={50}
          containerRef={wrapper}
          setAllItems={setAccounts}
        >
          {({ items }) => (
            <Stack gap={"16px"}>
              {cashedData &&
                !cashedData.all_have_hg_data &&
                isPlaybooksGeneration &&
                shouldHaveHGData && (
                  <Typography
                    variant="body1"
                    color="custom.textDarkBlue"
                    sx={{
                      border: "1px solid #FFD5A1",
                      backgroundColor: "#FFF7E5",
                      borderRadius: "6px",
                      padding: "16px",
                      lineHeight: "22px",
                    }}
                  >
                    Missing HG file for this campaign. Some sections won't be
                    available. Add it via resync.
                  </Typography>
                )}
              <Stack gap={"8px"}>
                <Stack gap={"12px"}>
                  <Typography
                    component="span"
                    variant="subtitle1"
                    sx={{ color: "#464646", fontWeight: 500 }}
                  >
                    {`Which accounts would you like to ${isPlaybooksGeneration ? "generate" : "delete"}
                     ${PlaybookPlanNames.playbooks} ${isPlaybooksGeneration ? "for" : "from"}?`}
                  </Typography>
                  <CustomInput
                    value={searchValue}
                    placeholder="Search"
                    prefixIcon={
                      isFetching && page === 1 ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <SearchIcon />
                      )
                    }
                    onChange={setSearchValue}
                    clearButton
                    minHeight="42px"
                  />
                </Stack>

                <Stack>
                  <Stack
                    ref={wrapper}
                    maxHeight={"320px"}
                    sx={{ overflowY: "auto" }}
                  >
                    {items.length === 0 && !isFetching && (
                      <Typography
                        variant="body1"
                        sx={{ color: "#797979", padding: "8px 12px" }}
                      >
                        No accounts with such name found
                      </Typography>
                    )}

                    {items.map(
                      ({
                        name,
                        id,
                        available_for_playbook_count,
                        members_with_playbook_count,
                        has_hg_data,
                      }) => (
                        <ListItem
                          key={id}
                          fullName={name}
                          onClick={() => selectMemberHandler(id)}
                          id={id}
                          checked={selected[id] || false}
                          totalCount={
                            isPlaybooksGeneration
                              ? available_for_playbook_count
                              : members_with_playbook_count
                          }
                          withAlert={!has_hg_data}
                          isDisabled={
                            isRemovingPlaybooks || isGeneratingPlaybooks
                          }
                        />
                      ),
                    )}
                    {isFetching && page !== 1 && (
                      <Stack
                        alignItems={"center"}
                        justifyContent={"center"}
                        flexGrow={1}
                        minHeight={"30px"}
                      >
                        <CircularProgress size={20} sx={{ color: "#1C3C6C" }} />
                      </Stack>
                    )}
                  </Stack>

                  {items.length > 0 && (
                    <>
                      <Divider style={{ color: "#D9D9D9", margin: 0 }} />
                      <ListItem
                        fullName={
                          isGeneratingPlaybooks
                            ? `Select All: ${data?.total_members || 0}`
                            : "Select All"
                        }
                        onClick={toggleAllHandler}
                        id="Select all"
                        checked={isSelectAll}
                        isDisabled={
                          isRemovingPlaybooks || isGeneratingPlaybooks
                        }
                      />
                    </>
                  )}
                </Stack>
              </Stack>

              {isPlaybooksGeneration && (
                <ManageSectionsBlock
                  adjustSection={`generated ${ManageSectionsModals.playbook}`}
                  manageSections={ManageSectionsModals.playbook}
                  hidePrevModalHandler={setIsHide}
                  closePrevModalHandler={onClose}
                  modal={ManageSectionsModals.playbook}
                />
              )}
            </Stack>
          )}
        </InfiniteScroll>
      </Stack>

      {isConfirmModal && (
        <ConfirmRemovePlaybooksModal
          onClose={() => setIsConfirmModal(false)}
          onSubmit={removePlaybooksHandler}
        />
      )}
    </ReuseModal>
  );
};
